import {EventBus} from "../../../common/ts/events/EventBus";
import {WhitelabelEvent} from "../../../common/ts/events/Events";
import {
    ShowRetentionCaptureModalRequestedEvent,
    ShowRetentionPopupMobileRequestedEvent
} from "../components/retention/email/main/events";
import {SerpClickSimilarAdsRequestedEvent, SerpLoadedEvent, SerpProjectsLoadedSuccessfullyEvent} from "./events";
import {SetItemEvent} from "../../../common/ts/core/localStorage";
import api from "../api";
import http from "../../../common/ts/core/http";
import dom from "../../../common/ts/core/dom";
import {FindIntent} from "../components/snippet/snippet";
import {ShowWebPushRequestedEvent} from "../components/retention/webpush/events";
import {ShowGoogleOneTapRequestedEvent} from "./googleOneTap/events";
import {Device} from "../../../common/ts/types/Device";
import {hasWebpushConsent, hasWebpushSupport} from "./webpush/webpush";
import {hasConsentForGoogleCookies, hasGoogleOneTapAlreadyBeenPrompted} from "./googleOneTap/googleOneTap";

let eventBus: EventBus;
const PROJECT_SNIPPET_BLOCK = '#project-snippet';

function init(ev: any = EventBus.getInstance()) {
    eventBus = ev;
    eventBus.subscribe(SerpLoadedEvent.TYPE, serpLoadedEventHandler);
    eventBus.subscribe(SerpClickSimilarAdsRequestedEvent.TYPE, serpClickSimilarAdsRequestedEventHandler);
    eventBus.subscribe(SerpProjectsLoadedSuccessfullyEvent.TYPE, serpProjectsLoadedSuccessfullyHandler);
}

export function serpLoadedEventHandler(event: WhitelabelEvent) {
    const { payload: {
        search,
        shouldShowEmailRetentionModal,
        shouldShowProjectSnippet,
        shouldRequestWebPushPermission,
        shouldShowGoogleOneTapOnLoad,
        storedSearches,
        operationType,
        filters,
        geoEntity,
        pageviewId,
        device,
        isGDPRCountry,
    }} = event as SerpLoadedEvent

    if (shouldShowProjectSnippet) {
        const formattedSearch = search.replace(" ", "+").toLowerCase();
        eventBus.emit(
            new http.PostEvent({
                body: {
                    operationType: operationType,
                    textSearch: search,
                    filters: filters,
                    geoEntity: geoEntity,
                }, successEvent: {eventClass: SerpProjectsLoadedSuccessfullyEvent},
                url: "/api/serp-projects?search_terms="+ formattedSearch + "&page=1" + "&t_pvid=" + pageviewId
            })
        )
    } else {
        eventBus.emit(new dom.HideEvent({selector: PROJECT_SNIPPET_BLOCK}));
    }

    if (shouldRequestWebPushPermission && hasWebpushSupport()) {
        eventBus.emit(new ShowWebPushRequestedEvent({
            search,
            pageviewId,
            device,
            showGoogleOneTapAfter: shouldShowGoogleOneTapOnLoad,
        }));
    }

    if (
        shouldShowGoogleOneTapOnLoad &&
        (
            device == Device.DESKTOP ||
            ((device == Device.MOBILE) && hasWebpushConsent())
        ) &&
        (
            !isGDPRCountry || hasConsentForGoogleCookies()
        ) &&
        !hasGoogleOneTapAlreadyBeenPrompted()
    ) {
        eventBus.emit(new ShowGoogleOneTapRequestedEvent())
    }

    if (shouldShowEmailRetentionModal && !hasSearchAlreadyBeenVisited(search, storedSearches)) {
        if (device === Device.MOBILE) {
            if (hasWebpushConsent() && hasConsentForGoogleCookies() && hasGoogleOneTapAlreadyBeenPrompted()) {
                eventBus.emit(new ShowRetentionPopupMobileRequestedEvent());
            }
        } else {
            eventBus.emit(new ShowRetentionCaptureModalRequestedEvent());
        }

        updateStoredSearches(search, storedSearches);

        eventBus.emit(new dom.UpdateDataAttributeEvent({
            elementSelector: "#js-email-retention-form",
            attributeName: "data-form-origin-opened-from",
            attributeValue: "desktop-auto-show",
        }));
    }
}

function hasSearchAlreadyBeenVisited(search: string, storedSearches: string | null) {
    if (storedSearches) {
        const parsedSearches = JSON.parse(storedSearches) || [];
        return parsedSearches.includes(search);
    }

    return false;
}

function updateStoredSearches(search: string, storedSearches: string | null) {
    let searchesToSave: string[];
    if (storedSearches) {
        const parsedSearches = JSON.parse(storedSearches);
        parsedSearches.push(search);
        searchesToSave = parsedSearches;
    } else {
        searchesToSave = [search];
    }

    if (searchesToSave.length > 0) {
        eventBus.emit(new SetItemEvent({ key: "visitedSearches", value: JSON.stringify(searchesToSave) }));
    }
}

type SerpClickSimilarAdsRequestParams = {
    id: string,
    number: number,
    ids: string,
    currentOrigin: string,
    pageViewId: string,
    findIntent: FindIntent,
};

export function serpClickSimilarAdsRequestedEventHandler(event: WhitelabelEvent) {
    const { payload: {
        adId,
        search,
        ids,
        pageSectionId,
        numberOfAds,
        pageViewId,
        successEventClass,
        findIntent,
    } } = event as SerpClickSimilarAdsRequestedEvent;

    const idsParam = ids == null ? '' : ids.join(',')

    const params = {
        id: adId,
        number: numberOfAds,
        currentOrigin: pageSectionId,
        pageViewId: pageViewId,
        ids: idsParam,
        findIntent: findIntent,
    } as SerpClickSimilarAdsRequestParams;

    // @ts-ignore
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key])

    eventBus.emit(
        api.events.retrieveSerpClickSimilarAds(
            params,
            {
                eventClass: successEventClass,
                payload: (event as SerpClickSimilarAdsRequestedEvent).payload
            }
        )
    )
}

export function serpProjectsLoadedSuccessfullyHandler(event: WhitelabelEvent) {
    const { payload: {
        response
    } } = event as SerpProjectsLoadedSuccessfullyEvent;

    const projectSnippetBlock = dom.select(PROJECT_SNIPPET_BLOCK) as HTMLElement | null
    if (projectSnippetBlock != null && response != "") {
        projectSnippetBlock.innerHTML = response
    } else {
        eventBus.emit(new dom.HideEvent({selector: PROJECT_SNIPPET_BLOCK}));
    }
}

export default {
    init,
};
